import { ecLean, handBack, type JanusCtx, type Row } from '../ability/context';
import { encounterField } from '../../shared/array-types';
import { ftClockColsFromArr } from '../../shared/campaign-clock';
import { surfaceField } from '../compile/from-base-encounter-media';
import { fromBaseRecord } from '../compile/from-base-context';
import { readItemField } from '../compile/item-field-reader';
import type { SpawnRowPlan } from '../spawn/row-plan';
import { applyEndTurnTruth } from '../turns/end-turn';
import { removeStatusFromRow } from '../statuses/expiry';
import { deathRawCreatureBase } from './departure';
import { inventoryRowList } from '../compile/inventory-raw-readers';
import { fromBaseUuid } from '../compile/from-base-context';

/** Reset owns every watch armed BY the actor, regardless of whom it watches. The spell-cast
 * counterspell reader answers the opposite relation and must never supply these rows. */
function appendOwnedWatches(ctx: JanusCtx, plan: SpawnRowPlan, actorId: string): void {
    const raw = deathRawCreatureBase(actorId, [ctx.encounter, ...ctx.members.values()]);
    if (!raw || !Array.isArray(raw.counterspell_watches)) handBack('death:owned-watch-inputs-not-held');
    const encounterId = String(encounterField(ctx.encounter, 'encounter_id') ?? '').toUpperCase();
    for (const watch of inventoryRowList(raw.counterspell_watches)) {
        if (Number(readItemField(watch, 'active') ?? 0) !== 1 || readItemField(watch, 'trigger_type') !== 'counterspell'
            || String(readItemField(watch, 'caster_encounter_creature_id') ?? '').toUpperCase() !== actorId
            || String(readItemField(watch, 'encounter_id') ?? '').toUpperCase() !== encounterId) continue;
        const id = readItemField(watch, 'trigger_id');
        if (!fromBaseUuid(id)) handBack('death:owned-watch-identity-not-held');
        plan.writes.push({ table: 'encounter_creature_triggers', op: 'update', row: { trigger_id: id, active: 0 },
            before: { active: 1, caster_encounter_creature_id: actorId } });
    }
}

/** Persist the already-decided turn-death resolver output inside its enclosing row plan.
 * Shared by a source replacement and the death tail; this does not decide a second boundary. */
export function appendTurnDeathRows(ctx: JanusCtx, plan: SpawnRowPlan, dyingId: string, reason = 'death:turn'): void {
    if (ctx.turnTransition?.dying_id === dyingId) {
        const transition = ctx.turnTransition, next = ctx.members.get(transition.next_id);
        if (!next) handBack(`${reason}-next-not-held`);
        const beforeRev = Number(ecLean(next!, 'movement_rev') ?? 0);
        plan.writes.push({ table: 'encounters', op: 'update', row: { encounter_id: String(encounterField(ctx.encounter, 'encounter_id')),
            ...transition.encounter }, before: { current_encounter_creature_id_turn: dyingId, current_round: transition.round_before } });
        const reset = transition.next;
        if (reset.refresh !== false) plan.writes.push({ table: 'encounter_creature_turn_resets', op: 'update', row: { encounter_creature_id: transition.next_id,
            aps: reset.aps, reaction: reset.reaction, weapon_attack: 0, head_attack: 0, movement_pct: 0, movement_rev: beforeRev + 1,
            ...(reset.aps_total != null ? { aps_total: reset.aps_total } : {}) }, before: { movement_rev: beforeRev } });
        for (const removal of [...reset.status_remove, ...transition.others.flatMap(row => row.status_remove)]) {
            if (!plan.writes.some(write => write.table === 'statuses' && readItemField(write.row, 'status_id') === removal.status_id))
                plan.writes.push({ table: 'statuses', op: 'update', row: { status_id: removal.status_id, status_deleted: 1 }, before: { status_deleted: 0 } });
        }
        appendOwnedWatches(ctx, plan, dyingId);
        if (reset.refresh !== false) appendOwnedWatches(ctx, plan, transition.next_id);
        if (transition.wrap) {
            const campaign = fromBaseRecord(ctx.encounter.campaign_arr) ? ctx.encounter.campaign_arr : null;
            const bucket = campaign && fromBaseRecord(campaign.campaigns) ? campaign.campaigns : campaign;
            const campaignId = bucket ? surfaceField(bucket, 'campaign_id') : null;
            if (typeof campaignId === 'string' && campaignId !== '') {
                const clock = ctx.read.campaignClock();
                if (!clock) handBack(`${reason}-clock-not-held`);
                const nextClock = ctx.read.addCampaignSeconds(clock!, transition.frame.clock_seconds);
                const beforeClock = ftClockColsFromArr(campaign);
                const row: Row = { campaign_id: campaignId }, before: Row = {};
                for (const unit of ['year', 'month', 'day', 'hour', 'minute', 'second'] as const) {
                    row['campaign_time_' + unit] = nextClock[unit]; before['campaign_time_' + unit] = beforeClock[unit];
                }
                plan.writes.push({ table: 'campaigns', op: 'update', row, before });
            }
        }
        ctx.spawnTurnTransition = transition;
        ctx.turnTransition = undefined;
    } else if (Number(encounterField(ctx.encounter, 'in_combat') ?? 0) === 1
        && String(encounterField(ctx.encounter, 'current_encounter_creature_id_turn') ?? '').toUpperCase() === dyingId) {
        // Native advance clears the dying holder's watches before its empty-successor return.
        appendOwnedWatches(ctx, plan, dyingId);
    }
    if (ctx.turnDeathNone === dyingId) ctx.turnDeathNone = undefined;
}

/** The death composer's boundary: save rows, apply the successor's existing browser frame, then
 * expose its reset/status/clock state to the rest of THIS action. The replacement lane retains
 * its existing converter until its full frame transport is wired. */
export function settleTurnDeath(ctx: JanusCtx, plan: SpawnRowPlan, dyingId: string): void {
    const transition = ctx.turnTransition;
    if (!transition || transition.dying_id !== dyingId) { appendTurnDeathRows(ctx, plan, dyingId); return; }
    const next = ctx.members.get(transition.next_id);
    const others = transition.others.map(row => ctx.members.get(row.actor_id));
    if (!next || others.some(row => !row)) handBack('death:turn-participants-not-held');
    const removals = [...transition.next.status_remove, ...transition.others.flatMap(row => row.status_remove)];
    const start = plan.writes.length;
    appendTurnDeathRows(ctx, plan, dyingId);
    applyEndTurnTruth({ ending: ctx.members.get(dyingId) ?? null, next: next!, others: others as Row[] },
        ctx.encounter, transition.frame, transition.next.refresh === false ? {} : { next: Number(ecLean(next!, 'movement_rev') ?? 0) + 1 }, ctx.read.definition);
    const removed = new Set(removals.map(row => row.status_id.toUpperCase()));
    for (const holder of [ctx.encounter, ...ctx.members.values()]) for (const statusId of removed) removeStatusFromRow(holder, statusId);
    ctx.statusesAdded = ctx.statusesAdded.filter(row => !row.status_id || !removed.has(row.status_id.toUpperCase()));
    ctx.rows.push({ roll_type_1: transition.start_log ?? 'start_turn', roll_type_2: null, roll_item_id: null,
        roll_by: transition.next_id, roll_to: null, roll_result: 0, roll_private: ctx.rollPrivate as 0 | 1 | 2 });
    for (const removal of removals) ctx.rows.push({ roll_type_1: removal.log_key, roll_type_2: null, roll_item_id: removal.item_id,
        roll_by: removal.log_by, roll_to: null, roll_result: 0, roll_private: ctx.rollPrivate as 0 | 1 | 2 });
    if (transition.frame.skipped?.length) ctx.rows.push(...transition.frame.skipped);
    const campaignWrite = plan.writes.slice(start).reverse().find(write => write.table === 'campaigns');
    if (transition.wrap && campaignWrite) {
        const campaign = fromBaseRecord(ctx.encounter.campaign_arr) ? ctx.encounter.campaign_arr : null;
        const bucket = campaign && fromBaseRecord(campaign.campaigns) ? campaign.campaigns : campaign;
        if (!bucket) handBack('death:turn-campaign-not-held');
        Object.assign(bucket!, campaignWrite.row);
        if (campaign && campaign !== bucket) for (const [key, value] of Object.entries(campaignWrite.row))
            if (Object.prototype.hasOwnProperty.call(campaign, key)) campaign[key] = value;
        const clock = ftClockColsFromArr(campaign);
        ctx.read = { ...ctx.read, campaignClock: () => ({ ...clock }) };
    }
}
